// src/pages/AdminSettlements.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../api";

/* ---------- Small utilities ---------- */
function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

const money = (n) =>
  Number(n || 0).toLocaleString("en-LK", { style: "currency", currency: "LKR" });

function fmtTimeHM(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "-";
  const t = String(timeStr).slice(0, 5);
  const d = new Date(`${dateStr}T${t}:00+05:30`);
  if (Number.isNaN(d.getTime())) return `${dateStr} ${t}`;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function toLKDateTime(dateStr, timeStr) {
  const t = String(timeStr || "00:00").slice(0, 5);
  return new Date(`${dateStr}T${t}:00+05:30`);
}

function minutesBetween(a, b) {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / 60000);
}

function statusBadge(status) {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border";
  if (status === "due")
    return cls(base, "bg-yellow-100 text-yellow-800 border-yellow-200");
  if (status === "missed")
    return cls(base, "bg-red-100 text-red-700 border-red-200");
  if (status === "paid")
    return cls(base, "bg-green-100 text-green-700 border-green-200");
  return cls(base, "bg-gray-100 text-gray-700 border-gray-200");
}

const TABS = [
  { key: "dueNow", label: "Due (≤ 1 hour)" },
  { key: "todayFuture", label: "Today (Future)" },
  { key: "missed", label: "Missed" },
  { key: "settled", label: "Settled" },
];

const PAGE_SIZES = [25, 50, 100, 200];

const VIEW_MODES = [
  { key: "trips", label: "Trips" },
  { key: "operators", label: "Operators" },
];

/* ---------- Component ---------- */
const AdminSettlements = () => {
  /* Filters */
  const [filter, setFilter] = useState({
    q: "", // operator/bus/from/to search
    minAmount: "",
    maxAmount: "",
  });
  const debouncedFilter = useDebouncedValue(filter, 450);

  /* Tabs + view + paging */
  const [tab, setTab] = useState("dueNow");
  const [viewMode, setViewMode] = useState("trips");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  /* Data */
  const [today, setToday] = useState("");
  const [data, setData] = useState({
    dueNow: [],
    todayFuture: [],
    missed: [],
    settled: [],
  });

  /* UI */
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busyTripKey, setBusyTripKey] = useState(null);
  const [bulkBusyKey, setBulkBusyKey] = useState(null); // operator bulk settle

  /* Modal: Settle single trip */
  const [settleModal, setSettleModal] = useState(null);
  const [bankRef, setBankRef] = useState("");
  const [note, setNote] = useState("");

  /* Operator sorting */
  const [opSort, setOpSort] = useState("-operatorDue"); // default: highest due first

  /* Cancel token */
  const abortRef = useRef(null);

  const fetchToday = async () => {
    setLoading(true);
    setErr(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await apiClient.get("/admin/settlements/today", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        signal: controller.signal,
      });

      setToday(res.data?.today || "");
      setData({
        dueNow: res.data?.dueNow || [],
        todayFuture: res.data?.todayFuture || [],
        missed: res.data?.missed || [],
        settled: res.data?.settled || [],
      });
    } catch (e) {
      if (e.name === "CanceledError") return;
      console.error("Failed to fetch settlements", e);
      setErr("Failed to fetch settlements.");
      setData({ dueNow: [], todayFuture: [], missed: [], settled: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto refresh (due window changes)
  useEffect(() => {
    const t = setInterval(() => {
      fetchToday();
    }, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset page when tab/view/filters change
  useEffect(() => {
    setPage(1);
  }, [tab, viewMode, debouncedFilter]);

  const rawList = useMemo(() => data?.[tab] || [], [data, tab]);

  const getRowStatus = (row) => {
    if (tab === "settled") return "paid";
    if (tab === "missed") return "missed";
    if (tab === "dueNow") return "due";
    return "future";
  };

  const countdownText = (row) => {
    if (!row?.date || !row?.departureTime) return "-";
    const dep = toLKDateTime(row.date, row.departureTime);
    const now = new Date();
    const mins = minutesBetween(now, dep);
    if (mins == null) return "-";
    if (mins < 0) return `Departed ${Math.abs(mins)} min ago`;
    if (mins === 0) return "Departing now";
    if (mins < 60) return `Departs in ${mins} min`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return `Departs in ${hrs}h ${rem}m`;
  };

  const filteredTrips = useMemo(() => {
    let arr = Array.isArray(rawList) ? rawList.slice() : [];

    const q = (debouncedFilter.q || "").trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) => {
        const s = [
          r.operatorName,
          r.operatorEmail,
          r.operatorMobile,
          r.busName,
          r.from,
          r.to,
          r.departureTime,
          r.date,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return s.includes(q);
      });
    }

    const minA = Number(debouncedFilter.minAmount || "");
    const maxA = Number(debouncedFilter.maxAmount || "");
    const hasMin = Number.isFinite(minA) && debouncedFilter.minAmount !== "";
    const hasMax = Number.isFinite(maxA) && debouncedFilter.maxAmount !== "";

    if (hasMin) arr = arr.filter((r) => Number(r.operatorDue || 0) >= minA);
    if (hasMax) arr = arr.filter((r) => Number(r.operatorDue || 0) <= maxA);

    // Sort by departure
    if (tab === "missed") {
      arr.sort(
        (a, b) =>
          toLKDateTime(b.date, b.departureTime) - toLKDateTime(a.date, a.departureTime)
      );
    } else {
      arr.sort(
        (a, b) =>
          toLKDateTime(a.date, a.departureTime) - toLKDateTime(b.date, b.departureTime)
      );
    }

    return arr;
  }, [rawList, debouncedFilter, tab]);

  const tripsPage = useMemo(() => {
    const totalCount = filteredTrips.length;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return { items: filteredTrips.slice(start, end), total: totalCount };
  }, [filteredTrips, page, pageSize]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(tripsPage.total / pageSize)),
    [tripsPage.total, pageSize]
  );

  const stats = useMemo(() => {
    const sum = (arr) => (arr || []).reduce((a, r) => a + Number(r.operatorDue || 0), 0);
    const sumFee = (arr) =>
      (arr || []).reduce((a, r) => a + Number(r.totalConvenienceFee || 0), 0);

    const dueAmt = sum(data.dueNow);
    const futAmt = sum(data.todayFuture);
    const missAmt = sum(data.missed);
    const setAmt = sum(data.settled);

    const feeDue = sumFee(data.dueNow);
    const feeFut = sumFee(data.todayFuture);
    const feeMiss = sumFee(data.missed);
    const feeSet = sumFee(data.settled);

    return {
      dueNow: { count: (data.dueNow || []).length, amount: dueAmt, fee: feeDue },
      todayFuture: { count: (data.todayFuture || []).length, amount: futAmt, fee: feeFut },
      missed: { count: (data.missed || []).length, amount: missAmt, fee: feeMiss },
      settled: { count: (data.settled || []).length, amount: setAmt, fee: feeSet },

      // "Business KPIs"
      todayTotalOperatorDue: dueAmt + futAmt + missAmt,
      todayTotalAdminFee: feeDue + feeFut + feeMiss,
      todaySettledOperator: setAmt,
      todaySettledAdminFee: feeSet,
    };
  }, [data]);

  /* ---------------- Operator aggregation ---------------- */
  const operatorRows = useMemo(() => {
    // Group ONLY the currently selected tab list (rawList) OR filteredTrips?
    // For "real world": group the filteredTrips so filters apply.
    const src = filteredTrips;

    const m = new Map();

    src.forEach((r) => {
      const operatorId =
        r.operatorId || r.operator?._id || r.operator || r.busOperatorId || "unknown";
      const operatorName =
        r.operatorName || r.operator?.fullName || r.operator?.name || "Operator";
      const operatorEmail = r.operatorEmail || r.operator?.email || "";
      const operatorMobile = r.operatorMobile || r.operator?.mobile || "";

      const key = String(operatorId);

      if (!m.has(key)) {
        m.set(key, {
          operatorId: key,
          operatorName,
          operatorEmail,
          operatorMobile,
          trips: [],
          tripsCount: 0,
          operatorDue: 0,
          adminFee: 0,
          dueCount: 0,
          missedCount: 0,
          futureCount: 0,
          paidCount: 0,
          nextDeparture: null,
        });
      }

      const row = m.get(key);
      row.trips.push(r);
      row.tripsCount += 1;
      row.operatorDue += Number(r.operatorDue || 0);
      row.adminFee += Number(r.totalConvenienceFee || 0);

      const st = getRowStatus(r);
      if (st === "due") row.dueCount += 1;
      else if (st === "missed") row.missedCount += 1;
      else if (st === "paid") row.paidCount += 1;
      else row.futureCount += 1;

      const dep = toLKDateTime(r.date, r.departureTime);
      if (!row.nextDeparture || dep < row.nextDeparture) row.nextDeparture = dep;
    });

    let arr = Array.from(m.values());

    // Sorting operators
    const sortKey = opSort?.slice(1);
    const desc = opSort?.startsWith("-");

    arr.sort((a, b) => {
      let av = 0;
      let bv = 0;

      if (sortKey === "operatorDue") {
        av = a.operatorDue;
        bv = b.operatorDue;
      } else if (sortKey === "adminFee") {
        av = a.adminFee;
        bv = b.adminFee;
      } else if (sortKey === "tripsCount") {
        av = a.tripsCount;
        bv = b.tripsCount;
      } else if (sortKey === "operatorName") {
        const as = String(a.operatorName || "").toLowerCase();
        const bs = String(b.operatorName || "").toLowerCase();
        return desc ? bs.localeCompare(as) : as.localeCompare(bs);
      } else {
        av = a.operatorDue;
        bv = b.operatorDue;
      }

      return desc ? bv - av : av - bv;
    });

    return arr;
  }, [filteredTrips, opSort]); // eslint-disable-line react-hooks/exhaustive-deps

  const operatorPage = useMemo(() => {
    const totalCount = operatorRows.length;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return { items: operatorRows.slice(start, end), total: totalCount };
  }, [operatorRows, page, pageSize]);

  const viewTotal = viewMode === "operators" ? operatorPage.total : tripsPage.total;
  const viewPages = useMemo(
    () => Math.max(1, Math.ceil(viewTotal / pageSize)),
    [viewTotal, pageSize]
  );

  const openSettleModal = (row) => {
    setSettleModal(row);
    setBankRef("");
    setNote("");
  };

  const settleTrip = async (row, extra = {}) => {
    const key = `${row.busId}|${row.date}|${row.departureTime}`;
    setBusyTripKey(key);

    await apiClient.post(
      "/admin/settlements/settle-trip",
      {
        busId: row.busId,
        date: row.date,
        departureTime: row.departureTime,
        bankRef: extra.bankRef || "",
        note: extra.note || "",
      },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );

    setBusyTripKey(null);
  };

  const confirmSettle = async () => {
    if (!settleModal) return;

    try {
      await settleTrip(settleModal, { bankRef, note });
      setSettleModal(null);
      setBankRef("");
      setNote("");
      await fetchToday();
    } catch (e) {
      console.error("Failed to settle trip", e);
      alert("Failed to settle trip.");
      setBusyTripKey(null);
    }
  };

  // Bulk settle for operator (uses existing endpoint per trip)
  const settleAllForOperator = async (opRow) => {
    if (!opRow?.operatorId) return;
    if (tab === "settled") return;

    const visibleTrips = (opRow.trips || []).filter((t) => {
      const st = getRowStatus(t);
      return st !== "paid"; // settle everything not paid in this tab filter
    });

    if (visibleTrips.length === 0) return;

    if (
      !window.confirm(
        `Settle ${visibleTrips.length} trip(s) for "${opRow.operatorName}"?`
      )
    )
      return;

    const bulkKey = `op|${opRow.operatorId}|${tab}`;
    setBulkBusyKey(bulkKey);

    try {
      // sequential calls (safe)
      for (const t of visibleTrips) {
        // eslint-disable-next-line no-await-in-loop
        await settleTrip(t, {
          bankRef: "",
          note: `bulk settle (${tab})`,
        });
      }
      await fetchToday();
    } catch (e) {
      console.error("Bulk settle failed", e);
      alert("Bulk settle failed (check console).");
    } finally {
      setBulkBusyKey(null);
      setBusyTripKey(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold">💸 Admin Settlements</h2>
          <p className="text-sm text-gray-600">
            Today only ({today || "—"}) • Rule: due time = departure − 1 hour
          </p>
          <p className="text-xs text-gray-500 mt-1">
            KPIs: Today due (operator) {money(stats.todayTotalOperatorDue)} • Today admin fee{" "}
            {money(stats.todayTotalAdminFee)} • Settled {money(stats.todaySettledOperator)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View switch */}
          <div className="flex rounded-lg border overflow-hidden bg-white">
            {VIEW_MODES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setViewMode(v.key)}
                className={cls(
                  "px-3 py-2 text-sm transition",
                  viewMode === v.key ? "bg-gray-900 text-white" : "hover:bg-gray-50"
                )}
              >
                {v.label}
              </button>
            ))}
          </div>

          <button
            className="border px-3 py-2 rounded bg-white hover:bg-gray-50 transition disabled:opacity-50"
            onClick={fetchToday}
            disabled={loading}
            title="Refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="text-sm text-gray-600">Due (≤ 1 hour)</div>
          <div className="text-xl font-bold">{stats.dueNow.count}</div>
          <div className="text-sm text-gray-700">{money(stats.dueNow.amount)}</div>
          <div className="text-xs text-gray-500">Fee: {money(stats.dueNow.fee)}</div>
        </div>
        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="text-sm text-gray-600">Today (Future)</div>
          <div className="text-xl font-bold">{stats.todayFuture.count}</div>
          <div className="text-sm text-gray-700">{money(stats.todayFuture.amount)}</div>
          <div className="text-xs text-gray-500">Fee: {money(stats.todayFuture.fee)}</div>
        </div>
        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="text-sm text-gray-600">Missed</div>
          <div className="text-xl font-bold">{stats.missed.count}</div>
          <div className="text-sm text-gray-700">{money(stats.missed.amount)}</div>
          <div className="text-xs text-gray-500">Fee: {money(stats.missed.fee)}</div>
        </div>
        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="text-sm text-gray-600">Settled</div>
          <div className="text-xl font-bold">{stats.settled.count}</div>
          <div className="text-sm text-gray-700">{money(stats.settled.amount)}</div>
          <div className="text-xs text-gray-500">Fee: {money(stats.settled.fee)}</div>
        </div>
      </div>

      {/* Tabs + Filters */}
      <div className="rounded-xl border bg-white shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cls(
                "px-3 py-2 rounded-lg border transition",
                tab === t.key
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white hover:bg-gray-50"
              )}
            >
              {t.label}{" "}
              <span className={cls("text-xs ml-1", tab === t.key ? "opacity-90" : "opacity-70")}>
                ({(data?.[t.key] || []).length})
              </span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <input
            type="text"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="Search operator / bus / route / time"
          />
          <input
            type="number"
            inputMode="decimal"
            value={filter.minAmount}
            onChange={(e) => setFilter((f) => ({ ...f, minAmount: e.target.value }))}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="Min operator due (LKR)"
          />
          <input
            type="number"
            inputMode="decimal"
            value={filter.maxAmount}
            onChange={(e) => setFilter((f) => ({ ...f, maxAmount: e.target.value }))}
            className="border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            placeholder="Max operator due (LKR)"
          />
          <button
            className="border px-3 py-2 rounded bg-gray-50 hover:bg-gray-100 transition"
            onClick={() => setFilter({ q: "", minAmount: "", maxAmount: "" })}
          >
            Clear
          </button>
        </div>

        {viewMode === "operators" && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs text-gray-500">Sort operators:</span>
            <button
              className={cls(
                "px-2 py-1 rounded border text-xs",
                opSort === "-operatorDue" ? "bg-gray-900 text-white border-gray-900" : "bg-white"
              )}
              onClick={() => setOpSort("-operatorDue")}
              type="button"
            >
              Operator Due ↓
            </button>
            <button
              className={cls(
                "px-2 py-1 rounded border text-xs",
                opSort === "+operatorDue" ? "bg-gray-900 text-white border-gray-900" : "bg-white"
              )}
              onClick={() => setOpSort("+operatorDue")}
              type="button"
            >
              Operator Due ↑
            </button>
            <button
              className={cls(
                "px-2 py-1 rounded border text-xs",
                opSort === "-adminFee" ? "bg-gray-900 text-white border-gray-900" : "bg-white"
              )}
              onClick={() => setOpSort("-adminFee")}
              type="button"
            >
              Admin Fee ↓
            </button>
            <button
              className={cls(
                "px-2 py-1 rounded border text-xs",
                opSort === "-tripsCount" ? "bg-gray-900 text-white border-gray-900" : "bg-white"
              )}
              onClick={() => setOpSort("-tripsCount")}
              type="button"
            >
              Trips ↓
            </button>
            <button
              className={cls(
                "px-2 py-1 rounded border text-xs",
                opSort === "+operatorName" ? "bg-gray-900 text-white border-gray-900" : "bg-white"
              )}
              onClick={() => setOpSort("+operatorName")}
              type="button"
            >
              Name A→Z
            </button>
          </div>
        )}
      </div>

      {/* Paging */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">
          {loading ? "Loading…" : `Total: ${viewTotal.toLocaleString()}`}
          <span className="ml-2 text-xs text-gray-500">(auto-refresh every 45s)</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Rows:</label>
          <select
            className="border px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <button
            className="border px-2 py-1 rounded disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} / {viewPages}
          </span>
          <button
            className="border px-2 py-1 rounded disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(viewPages, p + 1))}
            disabled={page >= viewPages || loading}
          >
            Next
          </button>
        </div>
      </div>

      {/* VIEW: Operators */}
      {viewMode === "operators" ? (
        <div className="overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Operator
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Trips
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Operator Due
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Admin Fee
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Breakdown
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Next Departure
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="border px-3 py-2">
                        <div className="h-3 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : err ? (
                <tr>
                  <td colSpan={7} className="border px-3 py-6 text-center text-red-700">
                    {err}
                  </td>
                </tr>
              ) : operatorPage.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="border px-3 py-6 text-center text-gray-600">
                    No operators found for this tab/filters.
                  </td>
                </tr>
              ) : (
                operatorPage.items.map((op) => {
                  const bulkKey = `op|${op.operatorId}|${tab}`;
                  const bulkBusy = bulkBusyKey === bulkKey;

                  return (
                    <tr key={op.operatorId} className="hover:bg-gray-50">
                      <td className="border px-3 py-2">
                        <div className="font-semibold">{op.operatorName}</div>
                        <div className="text-xs text-gray-600">
                          {op.operatorEmail || op.operatorMobile ? (
                            <>
                              {op.operatorEmail || ""}
                              {op.operatorEmail && op.operatorMobile ? " • " : ""}
                              {op.operatorMobile || ""}
                            </>
                          ) : (
                            "—"
                          )}
                        </div>
                      </td>
                      <td className="border px-3 py-2">{op.tripsCount}</td>
                      <td className="border px-3 py-2 font-semibold">{money(op.operatorDue)}</td>
                      <td className="border px-3 py-2">{money(op.adminFee)}</td>
                      <td className="border px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                          {op.dueCount ? (
                            <span className={statusBadge("due")}>DUE {op.dueCount}</span>
                          ) : null}
                          {op.futureCount ? (
                            <span className={statusBadge("future")}>FUTURE {op.futureCount}</span>
                          ) : null}
                          {op.missedCount ? (
                            <span className={statusBadge("missed")}>MISSED {op.missedCount}</span>
                          ) : null}
                          {op.paidCount ? (
                            <span className={statusBadge("paid")}>PAID {op.paidCount}</span>
                          ) : null}
                          {!op.dueCount && !op.futureCount && !op.missedCount && !op.paidCount ? (
                            <span className={statusBadge("future")}>—</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="border px-3 py-2">
                        {op.nextDeparture ? op.nextDeparture.toLocaleString() : "—"}
                      </td>
                      <td className="border px-3 py-2">
                        {tab === "settled" ? (
                          <span className="text-gray-500">—</span>
                        ) : (
                          <button
                            className={cls(
                              "px-3 py-1 rounded text-white",
                              tab === "missed"
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-blue-600 hover:bg-blue-700",
                              bulkBusy ? "opacity-70 cursor-not-allowed" : ""
                            )}
                            disabled={bulkBusy}
                            onClick={() => settleAllForOperator(op)}
                            title="Settles all trips for this operator in the current tab (visible + filtered)"
                          >
                            {bulkBusy ? "Settling…" : "Settle All"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* VIEW: Trips */
        <div className="overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Trip
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Departure
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Countdown
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Bookings
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Operator Due
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Admin Fee
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Status
                </th>
                <th className="border px-3 py-2 bg-gray-100 sticky top-0 z-10 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="border px-3 py-2">
                        <div className="h-3 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : err ? (
                <tr>
                  <td colSpan={8} className="border px-3 py-6 text-center text-red-700">
                    {err}
                  </td>
                </tr>
              ) : tripsPage.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="border px-3 py-6 text-center text-gray-600">
                    No settlements found for this tab/filters.
                  </td>
                </tr>
              ) : (
                tripsPage.items.map((r) => {
                  const rowStatus = getRowStatus(r);
                  const badge = statusBadge(rowStatus);
                  const key = `${r.busId}|${r.date}|${r.departureTime}`;
                  const isBusy = busyTripKey === key;

                  return (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="border px-3 py-2">
                        <div className="font-semibold">{r.busName || "Bus"}</div>
                        <div className="text-xs text-gray-600">
                          {r.from || "-"} → {r.to || "-"} • {r.date || "-"}
                        </div>
                        {(r.operatorName || r.operatorEmail) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {r.operatorName ? `Operator: ${r.operatorName}` : ""}
                            {r.operatorName && r.operatorEmail ? " • " : ""}
                            {r.operatorEmail ? r.operatorEmail : ""}
                          </div>
                        )}
                      </td>
                      <td className="border px-3 py-2">{fmtTimeHM(r.date, r.departureTime)}</td>
                      <td className="border px-3 py-2">{countdownText(r)}</td>
                      <td className="border px-3 py-2">{Number(r.bookingsCount || 0)}</td>
                      <td className="border px-3 py-2 font-semibold">{money(r.operatorDue)}</td>
                      <td className="border px-3 py-2">{money(r.totalConvenienceFee)}</td>
                      <td className="border px-3 py-2">
                        <span className={badge}>
                          {rowStatus === "due"
                            ? "DUE"
                            : rowStatus === "missed"
                            ? "MISSED"
                            : rowStatus === "paid"
                            ? "SETTLED"
                            : "FUTURE"}
                        </span>
                      </td>
                      <td className="border px-3 py-2">
                        {tab === "settled" ? (
                          <span className="text-gray-500">—</span>
                        ) : (
                          <button
                            className={cls(
                              "px-3 py-1 rounded text-white",
                              tab === "missed"
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-blue-600 hover:bg-blue-700",
                              isBusy ? "opacity-70 cursor-not-allowed" : ""
                            )}
                            disabled={isBusy}
                            onClick={() => openSettleModal(r)}
                          >
                            {isBusy ? "Settling…" : "Settle"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Settle Modal (single trip) */}
      {settleModal && (
        <div className="mt-6 p-6 border bg-gray-50 rounded shadow-sm">
          <h3 className="text-lg font-bold mb-2">✅ Settle Trip</h3>

          <div className="text-sm text-gray-700">
            <div>
              <strong>Trip:</strong> {settleModal.busName || "Bus"} •{" "}
              {settleModal.from || "-"} → {settleModal.to || "-"}
            </div>
            <div>
              <strong>Date/Time:</strong> {settleModal.date} • {settleModal.departureTime}
            </div>
            <div>
              <strong>Operator Due:</strong>{" "}
              <span className="font-semibold">{money(settleModal.operatorDue)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs text-gray-600">Bank Reference (optional)</label>
              <input
                type="text"
                value={bankRef}
                onChange={(e) => setBankRef(e.target.value)}
                className="w-full border px-3 py-2 rounded"
                placeholder="e.g. BOC-TRX-12345"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full border px-3 py-2 rounded"
                placeholder="e.g. settled 1h before departure"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
              onClick={confirmSettle}
              disabled={
                busyTripKey ===
                `${settleModal.busId}|${settleModal.date}|${settleModal.departureTime}`
              }
            >
              Confirm Settle
            </button>
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              onClick={() => setSettleModal(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettlements;
